import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import { S3Query } from 'dashboard-services/api/external';
import { CommonService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import dayjs from 'dayjs';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export default(() => {
  const selectTotalSize = response => response.totalSize

  const useSearchFilesCount = ({ query, provider, shouldBeLimited, getQueryBuilder, enabled = true }) => {
    const searchQuery = DataLakeQuery.QUERIES.searchFiles(getQueryBuilder, { query, provider, size: 1 })

    return useQuery({
      ...searchQuery,
      queryKey: [...searchQuery.queryKey, 'COUNT'],
      queryFn: context => searchQuery.queryFn({ ...context, pageParam: 0 }),
      refetchInterval: () => shouldBeLimited(provider) ? undefined : 10000,
      enabled,
      select: selectTotalSize
    })
  }

  const useSearchFiles = ({ query, provider, withStatus, shouldBeLimited, getQueryBuilder, size = CommonService.getRequestSize(), enabled = true, onSelect, shouldRefetch = true, ...rest }) => 
    useInfiniteQuery({
      ...DataLakeQuery.QUERIES.searchFiles(
        getQueryBuilder
        ,
        {
          query,
          withStatus,
          size,
          provider
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited(provider) ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      enabled,
      select: onSelect,
      ...rest
    })

  const useSearchFilesMutation = ({ getQueryBuilder, query, withStatus, size, provider }) =>
    useMutation({
      ...DataLakeQuery.QUERIES.searchFilesMutation(
        getQueryBuilder,
        {
          query,
          withStatus,
          size,
          provider
        }
      )
    })

  const useDownloadFile = ({ id, getQueryBuilder, provider, newTab = false }) => 
    useMutation({
      ...DataLakeQuery.QUERIES.getFileDefinitionMutation(
        getQueryBuilder,
        {
          id,
          provider
        }
      ),
      onSuccess: response => {
        const location = response.headers.get("Location");
        if (location) {
          CommonService.openInNewTab(location, newTab);
        } else {
          console.error(`No location present in the response. Response was ${response}`)
        }
      }
    })

  const useDeleteFile = ({ id, getQueryBuilder, onSuccess }) => {
    const queryClient = useQueryClient()
    return useMutation({
      ...DataLakeQuery.QUERIES.deleteFile(
        getQueryBuilder,
        {
          id
        }
      ),
      onSuccess: response => {
        queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() })
        onSuccess?.(response)
      }
    })
  }

  const useUploadFileMetadata = ({ getQueryBuilder, fileName, fileType, groupName, fields, zipped }) =>  
    useMutation({
      ...DataLakeQuery.QUERIES.uploadFile(
        getQueryBuilder,
        {
          fileName,
          fileType,
          groupName,
          fields,
          zipped
        }
      )
    })


  const useUploadFileContent = ({ getQueryBuilder, url, image }) => 
    useMutation({
      ...S3Query.QUERIES.upload(
        getQueryBuilder,
        {
          url,
          image
        }
      )
    })

  const useFileUpload = ({ getQueryBuilder, fileName, fileType, groupName, fields, zipped, image }) => {
    const { mutateAsync: uploadFileMetadata, isPending: isMetadataPending } = useUploadFileMetadata({ getQueryBuilder, fileName, fileType, groupName, fields, zipped }),
          { mutateAsync: uploadFileContent, isPending: isContentPending } = useUploadFileContent({ getQueryBuilder, image }),
          simpleUpload = useCallback(({ image, fileName } = {}) => uploadFileMetadata({ fileName }).then(response => uploadFileContent({ url: response.location, image }).then(() => response)), [uploadFileContent, uploadFileMetadata])
    return {
      uploadFileMetadata,
      uploadFileContent,
      simpleUpload,
      isPending: isMetadataPending || isContentPending
    }
  }

  const useGetFileDefinitionMutation = ({ getQueryBuilder, id, ...rest }) =>
    useMutation({
      ...DataLakeQuery.QUERIES.getFileDefinitionMutation(
        getQueryBuilder,
        {
          id
        }
      ),
      ...rest
    })

  const useGetFileDefinition = ({ getQueryBuilder, id, ...props }) =>
    useQuery({
      ...DataLakeQuery.QUERIES.getFileDefinition(
        getQueryBuilder,
        { 
          id 
        }
      ),
      ...props
    })

  const useGetFileContent = ({ getQueryBuilder, id, ...props }) =>
    useQuery({
      ...DataLakeQuery.QUERIES.getFileContent(
        getQueryBuilder,
        { 
          id 
        }
      ),
      gcTime: 0, // disable caching for this query due to possibility of large files
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      ...props
    })

  const useGetFileContentMutation = ({ getQueryBuilder, url, ...props }) =>
    useMutation({
      ...S3Query.QUERIES.getMutation(
        getQueryBuilder,
        { url }
      ),
      ...props
    })

  const useGetFileContentById = ({ getQueryBuilder, id, ...props }) =>
    useMutation({
      ...DataLakeQuery.QUERIES.getFileContentMutation(
        getQueryBuilder,
        { id }
      ),
      ...props
    })

  const useOpenEditorBasedOnFile = ({ getQueryBuilder, file, date, output }) => {
    const query = useMemo(() => {
            let query = `groupName=${file?.groupName}&name=${file?.name}`
            if(date && !output) {
              query += `&arrivalTime<${dayjs.utc(date).valueOf()}`
            } else if(date && output) {
              query += `&arrivalTime>${dayjs.utc(date).valueOf()}`
            }
            return query;
          }, [date, file, output]),
          { mutateAsync: getFileSizeCall } = useGetFileDefinitionMutation({ getQueryBuilder }),
          onSelect = useCallback(response => response.pages?.[0], []),
          { refetch: searchFilesCall } = useSearchFiles({
            query,
            getQueryBuilder,
            onSelect,
            shouldRefetch: false,
            enabled: false,
            withStatus: false
          })
    return {
      searchFilesCall,
      getFileSizeCall
    }
  }

  const useReturnFileContent = ({ getQueryBuilder, fileName, groupName, id = undefined, onFileNotFound = undefined, extraQuery = undefined  }) => {
    const dispatch = useDispatch(),
          signals = useRef([]),
          getQueryBuilderLocal = useCallback(() => {
            const controller = new AbortController()
            signals.current.push(controller)
            return getQueryBuilder()
              .withSignal(controller.signal)
          }, [getQueryBuilder]),
          getQueryBuilderNoParsing = useCallback(() => 
            getQueryBuilderLocal()
              .withOnParse(response => Promise.resolve(response))
              .withShouldTimeOut(false)
          , [getQueryBuilderLocal]),
          query = useMemo(() => `groupName=${groupName}&name=${fileName}${extraQuery ? `&${extraQuery}` : ""}`, [fileName, extraQuery, groupName]),
          { mutateAsync: searchFilesCall } = useSearchFilesMutation({ getQueryBuilder: getQueryBuilderLocal, query, size: 1 }),
          { mutateAsync: getFileDefinitionCall } = useGetFileDefinitionMutation({ getQueryBuilder: getQueryBuilderNoParsing }),
          { mutateAsync: getFileContentCall } = useGetFileContentMutation({ getQueryBuilder: getQueryBuilderNoParsing }),
          getFile = useCallback((skipContent = false) => {
            if((!fileName || !groupName) && !id) {
              return Promise.resolve()
            }
            return (id ? 
              Promise.resolve(id) : 
              searchFilesCall()
                .then(response => response.items?.[0]?.fid)
            )
              .then(id => {
                if(!id) {
                  onFileNotFound ? onFileNotFound() : dispatch(globalActions.getMessageStore()).error(`File ${fileName} not found in group ${groupName}`);
                  return;
                }
                return getFileDefinitionCall({ id })
              })
              .then(response => ({
                location: response?.headers?.get("Location"),
                rawResponse: response
              }))
              .then(({ location, rawResponse }) => rawResponse?.json().then(r => ({ definition: r, location })))
              .then(({ location, definition } = {}) => {
                if(!location && !skipContent) {
                  return Promise.resolve()
                }
                if(skipContent) {
                  return Promise.resolve({ definition, location })
                }
                return getFileContentCall({ url: location }).then(r => ({
                  content: r,
                  definition,
                  location
                }))
              }
              )
          }, [fileName, getFileContentCall, getFileDefinitionCall, groupName, id, searchFilesCall, dispatch, onFileNotFound])

    useEffect(() => () => {
      signals?.current?.forEach(s => s?.abort())
    }, [])
    return getFile;
  }

  const useTransferFilesOwnership = ({ getQueryBuilder, fileIds, currentOwnerUser, targetOwnerUser, transferAll, ...rest }) =>
    useMutation({
      ...DataLakeQuery.QUERIES.transferFilesOwnership(
        getQueryBuilder,
        {
          fileIds, 
          currentOwnerUser, 
          targetOwnerUser, 
          transferAll
        }
      ),
      ...rest
    })

  return {
    useSearchFilesCount,
    useSearchFiles,
    useDeleteFile,
    useDownloadFile,
    useFileUpload,
    useOpenEditorBasedOnFile,
    useSearchFilesMutation,
    useGetFileDefinitionMutation,
    useGetFileDefinition,
    useGetFileContent,
    useGetFileContentMutation,
    useUploadFileMetadata,
    useUploadFileContent,
    useReturnFileContent,
    useGetFileContentById,
    useTransferFilesOwnership
  }
})()
